const parseCountdownInstant = (raw, ianaTimeZone) => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return NaN;

  if (typeof Temporal !== 'undefined') {
    try {
      return Temporal.Instant.from(trimmed).epochMilliseconds;
    } catch (_err) {
      try {
        const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
        const plain = Temporal.PlainDateTime.from(normalized);
        return plain.toZonedDateTime(ianaTimeZone).toInstant().epochMilliseconds;
      } catch (_e2) {
        return NaN;
      }
    }
  }

  if (/[zZ]$|[+-][0-9]{2}:[0-9]{2}/.test(trimmed)) {
    const ms = Date.parse(trimmed);
    return Number.isNaN(ms) ? NaN : ms;
  }

  const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const utcMs = Date.parse(`${normalized}Z`);
  return Number.isNaN(utcMs) ? NaN : utcMs;
};

class HeroCountdown extends HTMLElement {
  connectedCallback() {
    const raw = this.dataset.target ?? '';
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    const tz = this.dataset.timezone ? this.dataset.timezone.trim() : 'UTC';

    this._expiredText = this.dataset.expiredText || 'The drop has launched!';
    this._invalidText = this.dataset.invalidText || 'Set a valid drop date.';

    if (!trimmed) {
      this.innerHTML = '';
      this.hidden = true;
      return;
    }

    this._targetDate = parseCountdownInstant(trimmed, tz);

    if (Number.isNaN(this._targetDate)) {
      this.innerHTML =
        '<p class="hero-countdown__expired" role="alert">' + this._invalidText + '</p>';
      return;
    }

    this._daysEl = this.querySelector('[data-countdown-days]');
    this._hoursEl = this.querySelector('[data-countdown-hours]');
    this._minutesEl = this.querySelector('[data-countdown-minutes]');
    this._secondsEl = this.querySelector('[data-countdown-seconds]');

    this._interval = null;
    this._tick();
    this._interval = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  _tick() {
    const diff = this._targetDate - Date.now();

    if (diff <= 0) {
      clearInterval(this._interval);
      this.innerHTML =
        '<p class="hero-countdown__expired">' + this._expiredText + '</p>';
      return;
    }

    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    this._daysEl.textContent = String(days).padStart(2, '0');
    this._hoursEl.textContent = String(hours).padStart(2, '0');
    this._minutesEl.textContent = String(minutes).padStart(2, '0');
    this._secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('hero-countdown')) {
  customElements.define('hero-countdown', HeroCountdown);
}

class HeroCountdownCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.autoplay !== 'true') return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) return;

    const sliderEl = this.querySelector('[id^="Slider-"]');
    const slides = [...this.querySelectorAll('[id^="Slide-"]')];
    if (!sliderEl || slides.length < 2) return;

    const speedSec = Math.max(2, Number(this.dataset.speed) || 5);
    this._hcPaused = false;
    this._hcTimer = null;

    const advance = () => {
      if (this._hcPaused) return;
      const step = slides[1].offsetLeft - slides[0].offsetLeft;
      if (step <= 0) return;
      const current = Math.min(
        slides.length - 1,
        Math.max(0, Math.round(sliderEl.scrollLeft / step))
      );
      const next = (current + 1) % slides.length;
      sliderEl.scrollTo({ left: slides[next].offsetLeft, behavior: 'smooth' });
    };

    this._hcTimer = setInterval(advance, speedSec * 1000);

    const pause = () => {
      this._hcPaused = true;
    };

    const resume = () => {
      this._hcPaused = false;
    };

    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);
  }

  disconnectedCallback() {
    clearInterval(this._hcTimer);
    this._hcTimer = null;
  }
}

if (!customElements.get('hero-countdown-carousel')) {
  customElements.define('hero-countdown-carousel', HeroCountdownCarousel);
}
